import { makeStyles } from '@material-ui/styles'
import { take, takeRight } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill'
import { countWhile } from 'utils/arrayUtils'
import { logInfo } from 'utils/logUtils'
import { compareTags, TAGS_LIST_COMPRESSION_THRESHOLD } from 'utils/tagUtils'
import GrinChip from '../GrinChip'
import GrinMenu from '../menu/GrinMenu'
import GrinMenuItem from '../menu/GrinMenuItem'
import GrinTag from './GrinTag'
import TagsPicker from './TagsPicker'

const useStyles = makeStyles(() => ({
  root: {
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    flex: 1,
    paddingTop: 4,
    paddingBottom: 4
  },
  visibleTag: {
    marginRight: 10
  },
  assigneeTag: {
    marginLeft: 10,
    color: 'var(--text-color-16)'
  },
  showMoreChip: {
    color: 'var(--text-color-16)',
    cursor: 'pointer'
  },
  progressContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'var(--bg-color-28)',
    zIndex: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  showMoreContainer: {
    flexShrink: 0
  },
  addTagContainer: {
    flexShrink: 0
  }
}))

const TagsList = ({
  tags = [],
  assigneeTag = null,
  className,
  showIcon = true,
  canDeleteSystem = false,
  canDeleteCustom = true,
  canDeleteScan = true,
  canDeleteNeedsAttention = false,
  onDeleteTag,
  observeResize = true,
  compressionThreshold = TAGS_LIST_COMPRESSION_THRESHOLD
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { width, ref } = useResizeDetector()
  const userId = useSelector(state => state.profileReducer.doctor?.user?.id)
  const renderCount = useRef(0)
  const lastLogTime = useRef(Date.now())
  const isFirstRender = useRef(true)

  // Track render frequency and log periodically (every 30 seconds)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    renderCount.current += 1
    const now = Date.now()
    const LOG_INTERVAL_MS = 30000

    if (now - lastLogTime.current >= LOG_INTERVAL_MS && renderCount.current > 0) {
      logInfo('[TagsList] Render frequency report', {
        component: 'TagsList',
        event: 'render_frequency',
        renderCount: renderCount.current,
        intervalMs: LOG_INTERVAL_MS,
        tagsCount: tags?.length,
        userId
      })
      renderCount.current = 0
      lastLogTime.current = now
    }
  })

  const [displayedTags, setDisplayedTags] = useState([])
  const [hiddenTags, setHiddenTags] = useState([])
  const [hiddenTagsAmount, setHiddenTagsAmount] = useState(0)
  const [isHiddenTagsDropdownOpen, setIsHiddenTagsDropdownOpen] = useState(false)
  const [tagsWidth, setTagsWidth] = useState([])
  const [optimisticTags, setOptimisticTags] = useState([])

  const sortedTags = useMemo(() => {
    return [...tags, ...optimisticTags].sort(compareTags)
  }, [tags, optimisticTags])

  const tagsTooltipValues = useMemo(
    () => ({
      rm: t('pages.patients.selectedPatient.tags.remoteMonitoring'),
      rc: t('pages.patients.selectedPatient.tags.remoteConsultation')
    }),
    [t]
  )

  const isTagElement = useCallback(
    element =>
      element.classList.contains(classes.visibleTag) || element.firstChild.classList.contains(classes.visibleTag),
    [classes]
  )

  const isAssigneeTagElement = useCallback(
    element =>
      element.classList.contains(classes.assigneeTag) || element.firstChild.classList.contains(classes.assigneeTag),
    [classes.assigneeTag]
  )

  // Clear optimistic tags once the backend confirms them in patientTags
  useEffect(() => {
    setOptimisticTags(prev => {
      if (!prev.length) return prev
      const realValues = new Set(tags.map(t => t.value))
      const next = prev.filter(t => !realValues.has(t.value))
      return next.length === prev.length ? prev : next
    })
  }, [tags])

  const handleTagPending = useCallback(tag => {
    setOptimisticTags(prev => [...prev, tag])
    setTagsWidth([])
  }, [])

  const handleDelete = useCallback(
    tagValue => {
      const deletedTagIndex = sortedTags.findIndex(sortedTag => sortedTag.value === tagValue)
      const newTagsWidth = [...tagsWidth]
      newTagsWidth.splice(deletedTagIndex, 1)
      onDeleteTag(tagValue)
      setTagsWidth(newTagsWidth)
    },
    [onDeleteTag, sortedTags, tagsWidth]
  )

  const fitTagsToScreen = useCallback(
    (width, allTagsWidth, assigneeTagWidth, totalTagsCount) => {
      const fitInTagsCount = countWhile(
        allTagsWidth,
        nextWidth => nextWidth < width - assigneeTagWidth - compressionThreshold
      )

      const newHiddenAmount = totalTagsCount - fitInTagsCount
      setHiddenTagsAmount(newHiddenAmount)
    },
    [compressionThreshold]
  )

  const calculateAssigneTagWidth = useCallback(
    () => Array.from(ref.current.children).find(isAssigneeTagElement)?.offsetWidth || 0,
    [isAssigneeTagElement, ref]
  )

  const calculateAllTagsWidth = useCallback(() => {
    const tagElementWidths =
      !tagsWidth.length &&
      Array.from(ref.current.children)
        .filter(isTagElement)
        .map(element => element.offsetWidth)

    if (!tagsWidth.length && tagElementWidths.length) setTagsWidth(tagElementWidths)

    return tagElementWidths || tagsWidth
  }, [isTagElement, tagsWidth, ref])

  useEffect(() => {
    const updatedDisplayedTags = take(sortedTags, sortedTags.length - hiddenTagsAmount)
    const updatedHiddenTags = takeRight(sortedTags, hiddenTagsAmount)

    setDisplayedTags(updatedDisplayedTags)
    setHiddenTags(updatedHiddenTags)
  }, [sortedTags, hiddenTagsAmount])

  useEffect(() => {
    if (!width) {
      return
    }
    const startTime = performance.now()
    const allTagsWidth = calculateAllTagsWidth()
    const assigneeTagWidth = calculateAssigneTagWidth()

    fitTagsToScreen(width, allTagsWidth, assigneeTagWidth, sortedTags.length)

    const duration = performance.now() - startTime
    if (duration > 10) {
      logInfo('[TagsList] PERF: Resize effect slow', {
        component: 'TagsList',
        event: 'resize_effect',
        durationMs: duration,
        width,
        tagsCount: sortedTags.length
      })
    }
  }, [calculateAllTagsWidth, calculateAssigneTagWidth, fitTagsToScreen, width, sortedTags.length])

  const renderTag = useCallback(
    tag => (
      <GrinTag
        tooltipValue={tagsTooltipValues[tag?.value]}
        key={tag.value}
        tag={tag}
        className={[classes.tag, classes.visibleTag].join(' ')}
        canDeleteSystem={canDeleteSystem}
        canDeleteCustom={canDeleteCustom}
        canDeleteScan={canDeleteScan}
        canDeleteNeedsAttention={canDeleteNeedsAttention}
        onDelete={() => handleDelete(tag.value)}
        onClick={tag.onClick}
      />
    ),
    [tagsTooltipValues, classes, canDeleteSystem, canDeleteCustom, canDeleteScan, canDeleteNeedsAttention, handleDelete]
  )

  return (
    <div className={[classes.root, className].join(' ')} ref={ref}>
      {displayedTags.map(renderTag)}
      {hiddenTagsAmount > 0 && (
        <div className={classes.showMoreContainer}>
          <GrinMenu
            transformOrigin={{ vertical: -30, horizontal: 'left' }}
            disableHoverEffects
            triggerComponent={
              <div>
                <GrinChip
                  text={t('pages.patients.selectedPatient.tags.showMore', { amount: hiddenTags.length })}
                  className={classes.showMoreChip}
                />
              </div>
            }
            open={isHiddenTagsDropdownOpen}
            onClose={() => setIsHiddenTagsDropdownOpen(false)}
            onOpen={() => setIsHiddenTagsDropdownOpen(true)}
          >
            {hiddenTags.map(tag => (
              <div key={tag.value}>
                <GrinMenuItem nonActionable>{renderTag(tag)}</GrinMenuItem>
              </div>
            ))}
          </GrinMenu>
        </div>
      )}
      <div className={classes.addTagContainer}>
        <TagsPicker showIcon={showIcon} onTagPending={handleTagPending} />
      </div>
      {assigneeTag && (
        <GrinTag
          key={assigneeTag.value}
          tag={assigneeTag}
          showUnseenIndicator={assigneeTag.showUnseenIndicator}
          className={classes.assigneeTag}
          onDelete={null}
          onClick={assigneeTag.onClick}
        />
      )}
    </div>
  )
}

export default TagsList
